import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  Circle,
  X,
  Plus,
  Target,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Edit2,
  Check,
  Calendar,
  Search,
  Star,
  GripVertical,
  FolderInput,
} from "lucide-react";
import BulkAddGoalsModal from "./BulkAddGoalsModal";
import { formatDisplayDate } from "../utils/dateUtils";
import { renderRichText } from "../utils/richText";

// Completed sub-goals fold into a "Completed (N)" row once a goal has more than this many.
const SUBGOAL_FOLD_AT = 3;

const timeOf = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};

// Open sub-goals: dated first (earliest deadline on top), then undated in
// creation order. Done sub-goals: most recently ticked first; ones ticked
// before completion times were tracked follow in creation order.
const splitSubGoals = (list) => {
  const open = list
    .filter((s) => !s.completed)
    .sort((a, b) => {
      const ta = timeOf(a.deadline);
      const tb = timeOf(b.deadline);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return ta - tb;
    });
  const done = list
    .filter((s) => s.completed)
    .sort((a, b) => {
      const ta = timeOf(a.completedAt);
      const tb = timeOf(b.completedAt);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return tb - ta;
    });
  return { open, done };
};

const GoalsList = ({
  tabs,
  activeTabId,
  subGoals,
  onTabChange,
  onAddTab,
  onDeleteTab,
  onRenameTab,
  onReorderTabs,
  onAddGoal,
  onAddGoalsBulk,
  onToggleGoal,
  onDeleteGoal,
  onEditGoal,
  onMoveGoalUp,
  onMoveGoalDown,
  onUpdateGoalDeadline,
  onMoveGoalWithinTab,
  onMoveGoalBetweenTabs,
  onToggleGoalImportance,
  onAddSubGoal,
  onToggleSubGoal,
  onDeleteSubGoal,
  onEditSubGoal,
  onUpdateSubGoalDeadline,
  onToggleSubGoalImportance,
  getSubGoalsForGoal,
  areAllSubGoalsCompleted,
}) => {
  const [newGoalText, setNewGoalText] = useState("");
  const [newGoalDeadline, setNewGoalDeadline] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingTabId, setEditingTabId] = useState(null);
  const [editingTabName, setEditingTabName] = useState("");
  const [draggedGoalId, setDraggedGoalId] = useState(null);
  const [draggedFromTabId, setDraggedFromTabId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverTabId, setDragOverTabId] = useState(null);
  const [draggedTabId, setDraggedTabId] = useState(null);
  const [addingSubGoalToId, setAddingSubGoalToId] = useState(null);
  const [newSubGoalText, setNewSubGoalText] = useState("");
  const [newSubGoalDeadline, setNewSubGoalDeadline] = useState("");
  const [editingSubGoalId, setEditingSubGoalId] = useState(null);
  const [editingSubGoalText, setEditingSubGoalText] = useState("");
  const [completedExpanded, setCompletedExpanded] = useState(true);
  const [moveMenuGoalId, setMoveMenuGoalId] = useState(null);
  const [expandedDoneSubGoals, setExpandedDoneSubGoals] = useState({});

  const renderDoneSubGoalsToggle = (goalId, count) => {
    const open = !!expandedDoneSubGoals[goalId];
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpandedDoneSubGoals((prev) => ({ ...prev, [goalId]: !prev[goalId] }));
        }}
        className="flex items-center gap-1.5 text-xs font-semibold text-green-400 hover:text-green-300 px-2 py-1 rounded-lg hover:bg-gray-700/40 transition-colors"
        title={open ? "Hide completed sub-goals" : "Show completed sub-goals"}
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        Completed ({count})
      </button>
    );
  };

  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];

  // Global search across all tabs or filter current tab goals
  let displayedGoals;
  let isGlobalSearch = false;

  if (searchQuery) {
    // Search across all tabs (including sub-goals)
    isGlobalSearch = true;
    const allGoals = tabs.flatMap((tab) =>
      tab.goals.map((goal) => ({ ...goal, tabName: tab.name, tabId: tab.id }))
    );
    
    // Filter by goal text OR sub-goal text
    displayedGoals = allGoals.filter((goal) => {
      const goalMatches = goal.text.toLowerCase().includes(searchQuery.toLowerCase());
      const subGoalMatches = getSubGoalsForGoal(goal.id).some((subGoal) =>
        subGoal.text.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return goalMatches || subGoalMatches;
    });
  } else {
    // Show only current tab goals
    displayedGoals = activeTab ? activeTab.goals : [];
  }

  // For non-search mode, we need goals from active tab for operations
  const goals = activeTab ? activeTab.goals : [];

  // Sort goals: incomplete first (by deadline), then completed at the bottom
  const sortedGoals = [...displayedGoals].sort((a, b) => {
    // First, separate completed and incomplete goals
    if (a.completed && !b.completed) return 1; // Completed goes to bottom
    if (!a.completed && b.completed) return -1; // Incomplete stays on top

    // If both have same completion status, sort by deadline
    if (!a.deadline && !b.deadline) return 0;
    if (!a.deadline) return 1; // No deadline goes after goals with deadline
    if (!b.deadline) return -1;
    return new Date(a.deadline) - new Date(b.deadline);
  });

  // Completed goals are sorted by the date they were marked complete
  // (most recently completed first). Falls back to 0 for legacy goals that
  // were completed before completion dates were tracked.
  const completedGoals = sortedGoals
    .filter((g) => g.completed)
    .sort((a, b) => {
      const ta = a.completedAt ? new Date(a.completedAt).getTime() : 0;
      const tb = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      return tb - ta;
    });

  const handleAdd = () => {
    if (newGoalText.trim() && activeTab) {
      onAddGoal(activeTab.id, newGoalText.trim(), newGoalDeadline || null);
      setNewGoalText("");
      setNewGoalDeadline("");
    }
  };

  // Bulk add: pasting a multi-line list into the goal box opens a preview
  // instead of collapsing the lines into one goal.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const handleGoalPaste = (e) => {
    const text = e.clipboardData ? e.clipboardData.getData("text") : "";
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length >= 2 && onAddGoalsBulk) {
      e.preventDefault();
      setBulkText(text);
      setBulkOpen(true);
    }
  };
  const handleBulkConfirm = (items, deadline) => {
    if (activeTab && items.length) {
      onAddGoalsBulk(activeTab.id, items, deadline || null);
    }
    setBulkOpen(false);
    setBulkText("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAdd();
    }
  };

  const startEditing = (goal) => {
    setEditingId(goal.id);
    setEditingText(goal.text);
  };

  const saveEdit = () => {
    if (editingText.trim()) {
      onEditGoal(activeTab.id, editingId, editingText.trim());
      setEditingId(null);
      setEditingText("");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const handleEditKeyPress = (e) => {
    if (e.key === "Enter") {
      saveEdit();
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  };

  const startEditingTab = (tab) => {
    setEditingTabId(tab.id);
    setEditingTabName(tab.name);
  };

  const saveTabEdit = () => {
    if (editingTabName.trim()) {
      onRenameTab(editingTabId, editingTabName.trim());
      setEditingTabId(null);
      setEditingTabName("");
    }
  };

  const cancelTabEdit = () => {
    setEditingTabId(null);
    setEditingTabName("");
  };

  const handleTabEditKeyPress = (e) => {
    if (e.key === "Enter") {
      saveTabEdit();
    } else if (e.key === "Escape") {
      cancelTabEdit();
    }
  };

  const handleDeadlineChange = (goalId, deadline) => {
    onUpdateGoalDeadline(activeTab.id, goalId, deadline || null);
  };

  // Close the "move to tab" menu when clicking anywhere else.
  useEffect(() => {
    if (moveMenuGoalId == null) return undefined;
    const close = () => setMoveMenuGoalId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [moveMenuGoalId]);

  // Explicit "move this goal to another tab" dropdown (works without dragging).
  const renderMoveToTab = (goal, currentTabId) => {
    const others = tabs.filter((t) => t.id !== currentTabId);
    if (others.length === 0) return null;
    const open = moveMenuGoalId === goal.id;
    return (
      <div className="relative flex-shrink-0">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMoveMenuGoalId(open ? null : goal.id);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex-shrink-0"
          title="Move to another tab"
        >
          <FolderInput className="w-5 h-5 text-cyan-400 hover:text-cyan-300" />
        </button>
        {open && (
          <div
            className="absolute right-0 top-7 z-30 bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1 min-w-[9rem] max-h-52 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-500">
              Move to
            </div>
            {others.map((t) => (
              <button
                key={t.id}
                onClick={(e) => {
                  e.stopPropagation();
                  const target = tabs.find((x) => x.id === t.id);
                  onMoveGoalBetweenTabs(
                    currentTabId,
                    t.id,
                    goal.id,
                    target ? target.goals.length : 0
                  );
                  setMoveMenuGoalId(null);
                }}
                className="block w-full text-left px-3 py-1.5 text-sm text-cyan-200 hover:bg-gray-700 transition-colors"
              >
                {t.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Drag and drop handlers
  const handleDragStart = (e, goalId, tabId, index) => {
    setDraggedGoalId(goalId);
    setDraggedFromTabId(tabId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedGoalId(null);
    setDraggedFromTabId(null);
    setDragOverIndex(null);
    setDragOverTabId(null);
  };

  const handleDragOver = (e, goalId, tabId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    // Find the sorted index of this goal for visual feedback
    const sortedIndex = sortedGoals.findIndex((g) => g.id === goalId);
    setDragOverIndex(sortedIndex);
    setDragOverTabId(tabId);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the goals container, not just moving between items
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
      setDragOverTabId(null);
    }
  };

  const handleDrop = (e, targetGoalId, toTabId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedGoalId || !draggedFromTabId) return;

    // Don't do anything if dropping on the same goal
    if (draggedGoalId === targetGoalId) {
      setDraggedGoalId(null);
      setDraggedFromTabId(null);
      setDragOverIndex(null);
      setDragOverTabId(null);
      return;
    }

    const fromTab = tabs.find((t) => t.id === draggedFromTabId);
    const toTab = tabs.find((t) => t.id === toTabId);

    if (!fromTab || !toTab) return;

    // Get the actual (unsorted) array indices
    const fromIndex = fromTab.goals.findIndex((g) => g.id === draggedGoalId);
    const targetIndex = toTab.goals.findIndex((g) => g.id === targetGoalId);

    if (fromIndex === -1) return;

    if (targetIndex === -1) {
      // Target not found in actual array - append to end
      if (draggedFromTabId === toTabId) {
        // Already in this tab, just move to end if not already there
        if (fromIndex !== toTab.goals.length - 1) {
          onMoveGoalWithinTab(toTabId, fromIndex, toTab.goals.length - 1);
        }
      } else {
        onMoveGoalBetweenTabs(
          draggedFromTabId,
          toTabId,
          draggedGoalId,
          toTab.goals.length
        );
      }
    } else {
      // Insert above the target goal
      if (draggedFromTabId === toTabId) {
        // Moving within same tab
        // Calculate the correct insertion index accounting for the removal
        let insertIndex = targetIndex;

        // If we're moving the item down (fromIndex < targetIndex),
        // we need to account for the fact that removing fromIndex
        // will shift all items after it down by 1
        if (fromIndex < targetIndex) {
          insertIndex = targetIndex - 1;
        }
        // If we're moving up (fromIndex > targetIndex),
        // the target index doesn't change because we remove from after it

        // Only move if the position actually changes
        if (fromIndex !== insertIndex) {
          onMoveGoalWithinTab(toTabId, fromIndex, insertIndex);
        }
      } else {
        // Moving between tabs - insert above target in destination tab
        onMoveGoalBetweenTabs(
          draggedFromTabId,
          toTabId,
          draggedGoalId,
          targetIndex
        );
      }
    }

    setDraggedGoalId(null);
    setDraggedFromTabId(null);
    setDragOverIndex(null);
    setDragOverTabId(null);
  };

  const handleTabDragOver = (e, tabId) => {
    e.preventDefault();
    e.stopPropagation();
    // Reordering a tab onto another tab.
    if (draggedTabId && draggedTabId !== tabId) {
      setDragOverTabId(tabId);
      return;
    }
    // Moving a goal onto another tab (drop at end of that tab).
    if (draggedGoalId && draggedFromTabId !== tabId) {
      const tab = tabs.find((t) => t.id === tabId);
      if (tab) {
        setDragOverTabId(tabId);
        setDragOverIndex(tab.goals.length); // Drop at the end
      }
    }
  };

  const handleTabDrop = (e, tabId) => {
    e.preventDefault();
    e.stopPropagation();

    // Tab reorder takes priority over goal moves.
    if (draggedTabId) {
      if (draggedTabId !== tabId && onReorderTabs) {
        onReorderTabs(draggedTabId, tabId);
      }
      setDraggedTabId(null);
      setDragOverTabId(null);
      return;
    }

    if (!draggedGoalId || !draggedFromTabId || draggedFromTabId === tabId)
      return;

    const tab = tabs.find((t) => t.id === tabId);
    if (tab) {
      const toIndex = tab.goals.length;
      onMoveGoalBetweenTabs(draggedFromTabId, tabId, draggedGoalId, toIndex);
    }

    setDraggedGoalId(null);
    setDraggedFromTabId(null);
    setDragOverIndex(null);
    setDragOverTabId(null);
  };

  // Tab reorder drag (initiated from the grip handle on each tab).
  const handleTabReorderStart = (e, tabId) => {
    setDraggedTabId(tabId);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(tabId));
    } catch (_) {}
  };

  const handleTabReorderEnd = () => {
    setDraggedTabId(null);
    setDragOverTabId(null);
  };

  const completedCount = goals.filter((g) => g.completed).length;

  return (
    <div className="bg-gray-800 rounded-3xl p-4 sm:p-6 border-2 border-yellow-500/30 shadow-lg">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <h3 className="text-xl font-bold text-yellow-400">Goals</h3>
        </div>
        <span className="text-cyan-300 font-semibold">
          {completedCount}/{goals.length}
        </span>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search goals..."
            className="w-full bg-gray-700 text-cyan-200 pl-10 pr-4 py-2 rounded-xl border-2 border-gray-600 focus:border-yellow-500 outline-none transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-cyan-300 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {/* Search results count */}
        {searchQuery && (
          <p className="text-xs text-gray-400 mt-2">
            Found {displayedGoals.length} goal
            {displayedGoals.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl transition-all text-sm sm:text-base ${
              activeTabId === tab.id
                ? "bg-yellow-400 text-black font-semibold"
                : "bg-gray-700 text-cyan-200 hover:bg-gray-600"
            } ${
              dragOverTabId === tab.id && draggedFromTabId !== tab.id
                ? "ring-2 ring-cyan-400 ring-offset-2 ring-offset-gray-800"
                : ""
            }`}
            onDragOver={(e) => handleTabDragOver(e, tab.id)}
            onDrop={(e) => handleTabDrop(e, tab.id)}
            onDragLeave={handleDragLeave}
          >
            {editingTabId === tab.id ? (
              <input
                type="text"
                value={editingTabName}
                onChange={(e) => setEditingTabName(e.target.value)}
                onKeyDown={handleTabEditKeyPress}
                onBlur={saveTabEdit}
                autoFocus
                className="bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border-2 border-yellow-500 outline-none w-20 sm:w-24 text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <>
                <span
                  draggable
                  onDragStart={(e) => handleTabReorderStart(e, tab.id)}
                  onDragEnd={handleTabReorderEnd}
                  className="cursor-grab active:cursor-grabbing opacity-50 hover:opacity-100 transition-opacity -ml-1"
                  title="Drag to reorder tab"
                >
                  <GripVertical className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </span>
                <button
                  onClick={() => onTabChange(tab.id)}
                  className="flex-1 text-left"
                >
                  {tab.name}
                </button>
                {tabs.length > 1 && tab.name !== "To Do" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          `Delete tab "${tab.name}"? All goals in this tab will be deleted.`
                        )
                      ) {
                        onDeleteTab(tab.id);
                      }
                    }}
                    className="ml-1 hover:text-red-400 transition-colors"
                    title="Delete tab"
                  >
                    <X className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditingTab(tab);
                  }}
                  className="ml-1 hover:text-yellow-400 transition-colors"
                  title="Rename tab"
                >
                  <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </>
            )}
          </div>
        ))}
        <button
          onClick={onAddTab}
          className="flex items-center gap-1 px-4 py-2 rounded-xl bg-gray-700 text-cyan-200 hover:bg-gray-600 transition-all flex-shrink-0"
          title="Add new tab"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Goals List */}
      <div
        className="space-y-3 mb-4 max-h-96 overflow-y-auto"
        onDragLeave={handleDragLeave}
      >
        {sortedGoals.length === 0 ? (
          <div
            className="text-center py-8 text-gray-400"
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedGoalId) {
                handleTabDragOver(e, activeTab.id);
              }
            }}
            onDrop={(e) => {
              if (draggedGoalId) {
                handleTabDrop(e, activeTab.id);
              }
            }}
          >
            <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No goals yet. Add your first goal below!</p>
            {draggedGoalId && draggedFromTabId !== activeTab.id && (
              <p className="text-cyan-400 mt-2">
                Drop here to move goal to this tab
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Incomplete Goals Section */}
            {sortedGoals.filter((g) => !g.completed).length > 0 && (
              <>
                <div className="text-xs font-bold text-yellow-400 uppercase tracking-wider px-2">
                  Not Completed (
                  {sortedGoals.filter((g) => !g.completed).length})
                </div>
                {sortedGoals
                  .filter((g) => !g.completed)
                  .map((goal, sortedIndex) => {
                    const currentTabId = isGlobalSearch
                      ? goal.tabId
                      : activeTab.id;
                    const currentTabGoals = isGlobalSearch
                      ? tabs.find((t) => t.id === goal.tabId)?.goals || []
                      : goals;
                    const originalIndex = currentTabGoals.findIndex(
                      (g) => g.id === goal.id
                    );
                    const isDragging = draggedGoalId === goal.id;
                    const isDragOver =
                      dragOverIndex === sortedIndex &&
                      dragOverTabId === activeTab.id;
                    return (
                      <React.Fragment key={goal.id}>
                      <div
                        draggable
                        onDragStart={(e) => {
                          // Use original index for the actual move operation
                          handleDragStart(
                            e,
                            goal.id,
                            activeTab.id,
                            originalIndex
                          );
                        }}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDragOver(e, goal.id, activeTab.id);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDrop(e, goal.id, activeTab.id);
                        }}
                        className={`flex items-start gap-2 bg-gray-700 p-4 rounded-2xl group hover:bg-gray-600 transition-all cursor-move ${
                          isDragging ? "opacity-50" : ""
                        } ${
                          isDragOver
                            ? "ring-2 ring-cyan-400 ring-offset-2 ring-offset-gray-800 bg-gray-600"
                            : ""
                        }`}
                      >
                        {/* Reorder buttons */}
                        <div
                          className="flex flex-col gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          onDragOver={(e) => e.stopPropagation()}
                          onDrop={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMoveGoalUp(activeTab.id, originalIndex);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={originalIndex === 0}
                            className="disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            <ChevronUp className="w-4 h-4 text-cyan-400 hover:text-cyan-300" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMoveGoalDown(activeTab.id, originalIndex);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={originalIndex === goals.length - 1}
                            className="disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            <ChevronDown className="w-4 h-4 text-cyan-400 hover:text-cyan-300" />
                          </button>
                        </div>

                        {/* Checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Option A: Only allow checking if all sub-goals are completed
                            if (!goal.completed && !areAllSubGoalsCompleted(goal.id)) {
                              alert('Please complete all sub-goals first before marking this goal as complete.');
                              return;
                            }
                            onToggleGoal(currentTabId, goal.id);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="flex-shrink-0 mt-1"
                          title={!goal.completed && !areAllSubGoalsCompleted(goal.id) ? 'Complete all sub-goals first' : ''}
                        >
                          {goal.completed ? (
                            <CheckCircle2 className="w-6 h-6 text-yellow-400" />
                          ) : (
                            <Circle className={`w-6 h-6 ${areAllSubGoalsCompleted(goal.id) ? 'text-cyan-400' : 'text-gray-500'}`} />
                          )}
                        </button>

                        {/* Goal content */}
                        <div
                          className="flex-1 flex items-center gap-3"
                          onDragOver={(e) => e.stopPropagation()}
                          onDrop={(e) => e.stopPropagation()}
                        >
                          {/* Goal text or edit input */}
                          {editingId === goal.id ? (
                            <input
                              type="text"
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              onKeyDown={handleEditKeyPress}
                              onBlur={saveEdit}
                              autoFocus
                              className="flex-1 bg-gray-600 text-cyan-200 px-3 py-1 rounded-lg border-2 border-yellow-500 outline-none"
                            />
                          ) : (
                            <span
                              className={`flex-1 ${
                                goal.completed
                                  ? "line-through text-gray-400"
                                  : "text-cyan-200"
                              }`}
                            >
                              {renderRichText(goal.text, `goal-${goal.id}`, { collaborators: true })}
                            </span>
                          )}

                          {/* Deadline display and picker */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Add Sub-Goal Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAddingSubGoalToId(addingSubGoalToId === goal.id ? null : goal.id);
                                setNewSubGoalText("");
                                setNewSubGoalDeadline("");
                              }}
                              className="flex-shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                              title="Add sub-goal"
                            >
                              <Plus className={`w-4 h-4 ${addingSubGoalToId === goal.id ? 'text-cyan-400' : 'text-gray-400 hover:text-cyan-400'}`} />
                            </button>
                            
                            {goal.deadline ? (
                              <>
                                <span className="text-sm text-gray-400 flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatDisplayDate(new Date(goal.deadline))}
                                </span>
                                <input
                                  type="date"
                                  value={goal.deadline}
                                  onChange={(e) =>
                                    handleDeadlineChange(
                                      goal.id,
                                      e.target.value
                                    )
                                  }
                                  className="bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border border-gray-500 focus:border-yellow-500 outline-none text-sm opacity-0 group-hover:opacity-100 transition-opacity w-32"
                                  title="Change deadline"
                                />
                              </>
                            ) : (
                              <input
                                type="date"
                                value=""
                                onChange={(e) =>
                                  handleDeadlineChange(goal.id, e.target.value)
                                }
                                className="bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border border-gray-500 focus:border-yellow-500 outline-none text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Set deadline"
                              />
                            )}
                            {/* Importance star */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onToggleGoalImportance) {
                                  onToggleGoalImportance(activeTab.id, goal.id);
                                }
                              }}
                              className="flex-shrink-0"
                              title={goal.important ? "Remove from important" : "Mark as important"}
                            >
                              <Star
                                className={`w-5 h-5 transition-colors ${
                                  goal.important
                                    ? "text-yellow-400 fill-yellow-400"
                                    : "text-gray-500 hover:text-yellow-400"
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div
                          className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          onDragOver={(e) => e.stopPropagation()}
                          onDrop={(e) => e.stopPropagation()}
                        >
                          {renderMoveToTab(goal, currentTabId)}
                          {editingId === goal.id ? (
                            <button
                              onClick={saveEdit}
                              className="flex-shrink-0"
                              title="Save"
                            >
                              <Check className="w-5 h-5 text-green-400 hover:text-green-300" />
                            </button>
                          ) : (
                            <button
                              onClick={() => startEditing(goal)}
                              className="flex-shrink-0"
                              title="Edit"
                            >
                              <Edit2 className="w-5 h-5 text-yellow-400 hover:text-yellow-300" />
                            </button>
                          )}
                          <button
                            onClick={() => onDeleteGoal(currentTabId, goal.id)}
                            className="flex-shrink-0"
                            title="Delete"
                          >
                            <X className="w-5 h-5 text-red-400 hover:text-red-300" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Sub-Goals Section */}
                      {(() => {
                        const { open, done } = splitSubGoals(getSubGoalsForGoal(goal.id));
                        const foldDone = done.length > SUBGOAL_FOLD_AT;
                        const showDone = !foldDone || !!expandedDoneSubGoals[goal.id];
                        const renderSubGoalRow = (subGoal) => (
                              <div key={subGoal.id} className="flex items-start gap-2 bg-gray-700/50 p-3 rounded-xl group/subgoal hover:bg-gray-600/50 transition-all">
                                {/* Sub-goal checkbox */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleSubGoal(subGoal.id);
                                  }}
                                  className="flex-shrink-0 mt-0.5"
                                >
                                  {subGoal.completed ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                                  ) : (
                                    <Circle className="w-5 h-5 text-cyan-300" />
                                  )}
                                </button>

                                {/* Sub-goal content */}
                                <div className="flex-1 flex items-center gap-2">
                                  {editingSubGoalId === subGoal.id ? (
                                    <input
                                      type="text"
                                      value={editingSubGoalText}
                                      onChange={(e) => setEditingSubGoalText(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          onEditSubGoal(subGoal.id, editingSubGoalText);
                                          setEditingSubGoalId(null);
                                          setEditingSubGoalText('');
                                        } else if (e.key === 'Escape') {
                                          setEditingSubGoalId(null);
                                          setEditingSubGoalText('');
                                        }
                                      }}
                                      onBlur={() => {
                                        if (editingSubGoalText.trim()) {
                                          onEditSubGoal(subGoal.id, editingSubGoalText);
                                        }
                                        setEditingSubGoalId(null);
                                        setEditingSubGoalText('');
                                      }}
                                      autoFocus
                                      className="flex-1 bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border-2 border-cyan-500 outline-none text-sm"
                                    />
                                  ) : (
                                    <span className={`flex-1 text-sm ${subGoal.completed ? 'line-through text-gray-400' : 'text-cyan-100'}`}>
                                      {renderRichText(subGoal.text, `sg-${subGoal.id}`, { collaborators: true })}
                                    </span>
                                  )}

                                  {/* Sub-goal deadline */}
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {subGoal.deadline ? (
                                      <>
                                        <span className="text-xs text-gray-400 flex items-center gap-1">
                                          <Calendar className="w-3 h-3" />
                                          {formatDisplayDate(new Date(subGoal.deadline))}
                                        </span>
                                        <input
                                          type="date"
                                          value={subGoal.deadline}
                                          onChange={(e) => onUpdateSubGoalDeadline(subGoal.id, e.target.value)}
                                          className="bg-gray-600 text-cyan-200 px-1 py-0.5 rounded text-xs border border-gray-500 focus:border-cyan-500 outline-none opacity-0 group-hover/subgoal:opacity-100 transition-opacity w-24"
                                          title="Change deadline"
                                        />
                                      </>
                                    ) : (
                                      <input
                                        type="date"
                                        value=""
                                        onChange={(e) => onUpdateSubGoalDeadline(subGoal.id, e.target.value)}
                                        className="bg-gray-600 text-cyan-200 px-1 py-0.5 rounded text-xs border border-gray-500 focus:border-cyan-500 outline-none opacity-0 group-hover/subgoal:opacity-100 transition-opacity"
                                        title="Set deadline"
                                      />
                                    )}
                                    
                                    {/* Sub-goal importance star */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleSubGoalImportance(subGoal.id);
                                      }}
                                      className="flex-shrink-0"
                                      title={subGoal.important ? "Remove from important" : "Mark as important"}
                                    >
                                      <Star
                                        className={`w-4 h-4 transition-colors ${
                                          subGoal.important
                                            ? "text-yellow-400 fill-yellow-400"
                                            : "text-gray-500 hover:text-yellow-400"
                                        }`}
                                      />
                                    </button>
                                  </div>
                                </div>

                                {/* Sub-goal action buttons */}
                                <div className="flex gap-1 opacity-0 group-hover/subgoal:opacity-100 transition-opacity">
                                  {editingSubGoalId === subGoal.id ? (
                                    <button
                                      onClick={() => {
                                        if (editingSubGoalText.trim()) {
                                          onEditSubGoal(subGoal.id, editingSubGoalText);
                                        }
                                        setEditingSubGoalId(null);
                                        setEditingSubGoalText('');
                                      }}
                                      className="flex-shrink-0"
                                      title="Save"
                                    >
                                      <Check className="w-4 h-4 text-green-400 hover:text-green-300" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setEditingSubGoalId(subGoal.id);
                                        setEditingSubGoalText(subGoal.text);
                                      }}
                                      className="flex-shrink-0"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4 text-yellow-400 hover:text-yellow-300" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => onDeleteSubGoal(subGoal.id)}
                                    className="flex-shrink-0"
                                    title="Delete"
                                  >
                                    <X className="w-4 h-4 text-red-400 hover:text-red-300" />
                                  </button>
                                </div>
                              </div>
                        );
                        return (
                          <div className="ml-12 mt-2 space-y-2">
                            {open.map(renderSubGoalRow)}
                            {foldDone && renderDoneSubGoalsToggle(goal.id, done.length)}
                            {showDone && done.map(renderSubGoalRow)}
                            
                            {/* Add Sub-Goal Input - Only show when button clicked */}
                            {addingSubGoalToId === goal.id && (
                              <div className="flex items-center gap-2 bg-gray-700/30 p-2 rounded-xl border-2 border-cyan-500/50">
                                <input
                                  type="text"
                                  value={newSubGoalText}
                                  onChange={(e) => setNewSubGoalText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newSubGoalText.trim()) {
                                      onAddSubGoal(goal.id, newSubGoalText, newSubGoalDeadline || null);
                                      setNewSubGoalText("");
                                      setNewSubGoalDeadline("");
                                      setAddingSubGoalToId(null);
                                    } else if (e.key === 'Escape') {
                                      setNewSubGoalText("");
                                      setNewSubGoalDeadline("");
                                      setAddingSubGoalToId(null);
                                    }
                                  }}
                                  placeholder="Sub-goal text..."
                                  autoFocus
                                  className="flex-1 bg-transparent text-cyan-200 text-sm placeholder-gray-500 outline-none"
                                />
                                <input
                                  type="date"
                                  value={newSubGoalDeadline}
                                  onChange={(e) => setNewSubGoalDeadline(e.target.value)}
                                  className="bg-gray-600 text-cyan-200 px-2 py-1 rounded text-xs border border-gray-500 focus:border-cyan-500 outline-none w-24"
                                  title="Set deadline"
                                />
                                <button
                                  onClick={() => {
                                    if (newSubGoalText.trim()) {
                                      onAddSubGoal(goal.id, newSubGoalText, newSubGoalDeadline || null);
                                      setNewSubGoalText("");
                                      setNewSubGoalDeadline("");
                                      setAddingSubGoalToId(null);
                                    }
                                  }}
                                  className="flex-shrink-0 bg-cyan-500 hover:bg-cyan-600 p-1 rounded transition-colors"
                                  title="Add sub-goal"
                                  disabled={!newSubGoalText.trim()}
                                >
                                  <Check className="w-4 h-4 text-white" />
                                </button>
                                <button
                                  onClick={() => {
                                    setNewSubGoalText("");
                                    setNewSubGoalDeadline("");
                                    setAddingSubGoalToId(null);
                                  }}
                                  className="flex-shrink-0 bg-gray-600 hover:bg-gray-500 p-1 rounded transition-colors"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4 text-white" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      </React.Fragment>
                    );
                  })}
              </>
            )}

            {/* Completed Goals Section */}
            {completedGoals.length > 0 && (
              <>
                <div className="border-t-2 border-gray-700 my-4 pt-4">
                  <button
                    onClick={() => setCompletedExpanded((v) => !v)}
                    className="flex items-center gap-1.5 text-xs font-bold text-green-400 uppercase tracking-wider px-2 mb-3 hover:text-green-300 transition-colors"
                    title={completedExpanded ? "Collapse completed" : "Expand completed"}
                  >
                    {completedExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    Completed ({completedGoals.length})
                  </button>
                </div>
                {completedExpanded &&
                  completedGoals.map((goal, sortedIndex) => {
                    const currentTabId = isGlobalSearch
                      ? goal.tabId
                      : activeTab.id;
                    const currentTabGoals = isGlobalSearch
                      ? tabs.find((t) => t.id === goal.tabId)?.goals || []
                      : goals;
                    const originalIndex = currentTabGoals.findIndex(
                      (g) => g.id === goal.id
                    );
                    const isDragging = draggedGoalId === goal.id;
                    const isDragOver =
                      dragOverIndex === sortedIndex &&
                      dragOverTabId === currentTabId;
                    return (
                      <React.Fragment key={goal.id}>
                      <div
                        draggable
                        onDragStart={(e) => {
                          // Use original index for the actual move operation
                          handleDragStart(
                            e,
                            goal.id,
                            currentTabId,
                            originalIndex
                          );
                        }}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDragOver(e, goal.id, currentTabId);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDrop(e, goal.id, currentTabId);
                        }}
                        className={`flex items-start gap-2 bg-gray-700 p-4 rounded-2xl group hover:bg-gray-600 transition-all cursor-move ${
                          isDragging ? "opacity-50" : ""
                        } ${
                          isDragOver
                            ? "ring-2 ring-cyan-400 ring-offset-2 ring-offset-gray-800 bg-gray-600"
                            : ""
                        }`}
                      >
                        {/* Reorder buttons */}
                        <div
                          className="flex flex-col gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          onDragOver={(e) => e.stopPropagation()}
                          onDrop={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMoveGoalUp(currentTabId, originalIndex);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={originalIndex === 0 || isGlobalSearch}
                            className="disabled:opacity-30 disabled:cursor-not-allowed"
                            title={
                              isGlobalSearch
                                ? "Not available in search mode"
                                : "Move up"
                            }
                          >
                            <ChevronUp className="w-4 h-4 text-cyan-400 hover:text-cyan-300" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMoveGoalDown(currentTabId, originalIndex);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={
                              originalIndex === currentTabGoals.length - 1 ||
                              isGlobalSearch
                            }
                            className="disabled:opacity-30 disabled:cursor-not-allowed"
                            title={
                              isGlobalSearch
                                ? "Not available in search mode"
                                : "Move down"
                            }
                          >
                            <ChevronDown className="w-4 h-4 text-cyan-400 hover:text-cyan-300" />
                          </button>
                        </div>

                        {/* Checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleGoal(currentTabId, goal.id);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="flex-shrink-0 mt-1"
                        >
                          {goal.completed ? (
                            <CheckCircle2 className="w-6 h-6 text-yellow-400" />
                          ) : (
                            <Circle className="w-6 h-6 text-cyan-400" />
                          )}
                        </button>

                        {/* Goal content */}
                        <div
                          className="flex-1 flex items-center gap-3"
                          onDragOver={(e) => e.stopPropagation()}
                          onDrop={(e) => e.stopPropagation()}
                        >
                          {/* Goal text or edit input */}
                          {editingId === goal.id ? (
                            <input
                              type="text"
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              onKeyDown={handleEditKeyPress}
                              onBlur={saveEdit}
                              autoFocus
                              className="flex-1 bg-gray-600 text-cyan-200 px-3 py-1 rounded-lg border-2 border-yellow-500 outline-none"
                            />
                          ) : (
                            <div className="flex-1 flex items-center gap-2">
                              <span
                                className={`${
                                  goal.completed
                                    ? "line-through text-gray-400"
                                    : "text-cyan-200"
                                }`}
                              >
                                {renderRichText(goal.text, `goal-${goal.id}`, { collaborators: true })}
                              </span>
                              {isGlobalSearch && goal.tabName && (
                                <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded">
                                  {goal.tabName}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Completion date + deadline display */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {goal.completedAt && (
                              <span
                                className="text-xs text-green-400 flex items-center gap-1"
                                title="Date marked completed"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                {formatDisplayDate(new Date(goal.completedAt))}
                              </span>
                            )}
                            {goal.deadline ? (
                              <>
                                <span className="text-sm text-gray-400 flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatDisplayDate(new Date(goal.deadline))}
                                </span>
                                <input
                                  type="date"
                                  value={goal.deadline}
                                  onChange={(e) =>
                                    handleDeadlineChange(
                                      goal.id,
                                      e.target.value
                                    )
                                  }
                                  className="bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border border-gray-500 focus:border-yellow-500 outline-none text-sm opacity-0 group-hover:opacity-100 transition-opacity w-32"
                                  title="Change deadline"
                                />
                              </>
                            ) : (
                              <input
                                type="date"
                                value=""
                                onChange={(e) =>
                                  handleDeadlineChange(goal.id, e.target.value)
                                }
                                className="bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border border-gray-500 focus:border-yellow-500 outline-none text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Set deadline"
                              />
                            )}
                            {/* Importance star */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onToggleGoalImportance) {
                                  onToggleGoalImportance(currentTabId, goal.id);
                                }
                              }}
                              className="flex-shrink-0"
                              title={goal.important ? "Remove from important" : "Mark as important"}
                            >
                              <Star
                                className={`w-5 h-5 transition-colors ${
                                  goal.important
                                    ? "text-yellow-400 fill-yellow-400"
                                    : "text-gray-500 hover:text-yellow-400"
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div
                          className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          onDragOver={(e) => e.stopPropagation()}
                          onDrop={(e) => e.stopPropagation()}
                        >
                          {renderMoveToTab(goal, currentTabId)}
                          {editingId === goal.id ? (
                            <button
                              onClick={saveEdit}
                              className="flex-shrink-0"
                              title="Save"
                            >
                              <Check className="w-5 h-5 text-green-400 hover:text-green-300" />
                            </button>
                          ) : (
                            <button
                              onClick={() => startEditing(goal)}
                              className="flex-shrink-0"
                              title="Edit"
                            >
                              <Edit2 className="w-5 h-5 text-yellow-400 hover:text-yellow-300" />
                            </button>
                          )}
                          <button
                            onClick={() => onDeleteGoal(currentTabId, goal.id)}
                            className="flex-shrink-0"
                            title="Delete"
                          >
                            <X className="w-5 h-5 text-red-400 hover:text-red-300" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Sub-Goals Section for Completed Goals */}
                      {(() => {
                        const { open, done } = splitSubGoals(getSubGoalsForGoal(goal.id));
                        if (open.length + done.length === 0) return null;
                        const foldDone = done.length > SUBGOAL_FOLD_AT;
                        const showDone = !foldDone || !!expandedDoneSubGoals[goal.id];
                        const renderSubGoalRow = (subGoal) => (
                              <div key={subGoal.id} className="flex items-start gap-2 bg-gray-700/50 p-3 rounded-xl opacity-70">
                                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                <span className="flex-1 text-sm line-through text-gray-400">
                                  {renderRichText(subGoal.text, `sg-${subGoal.id}`, { collaborators: true })}
                                </span>
                                {subGoal.deadline && (
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDisplayDate(new Date(subGoal.deadline))}
                                  </span>
                                )}
                              </div>
                        );
                        return (
                          <div className="ml-12 mt-2 space-y-2">
                            {open.map(renderSubGoalRow)}
                            {foldDone && renderDoneSubGoalsToggle(goal.id, done.length)}
                            {showDone && done.map(renderSubGoalRow)}
                          </div>
                        );
                      })()}
                      </React.Fragment>
                    );
                  })}
              </>
            )}
          </>
        )}
      </div>

      <BulkAddGoalsModal
        isOpen={bulkOpen}
        rawText={bulkText}
        tabName={activeTab ? activeTab.name : ""}
        onConfirm={handleBulkConfirm}
        onClose={() => setBulkOpen(false)}
      />

      {/* Add Goal Input */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={newGoalText}
            onChange={(e) => setNewGoalText(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handleGoalPaste}
            placeholder="Add a new goal..."
            className="flex-1 bg-gray-700 text-cyan-200 px-4 py-3 rounded-2xl border-2 border-gray-600 focus:border-yellow-500 outline-none transition-colors placeholder-gray-500"
          />
          <button
            onClick={handleAdd}
            disabled={!newGoalText.trim()}
            className="bg-yellow-400 hover:bg-yellow-300 disabled:bg-gray-600 disabled:cursor-not-allowed text-black px-4 py-3 rounded-2xl font-semibold transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add
          </button>
        </div>
        <p className="text-[11px] text-gray-400 px-2">
          Tip: paste a multi-line list to add several goals at once — indented
          lines become sub-goals.
        </p>
        {/* Optional deadline field */}
        <div className="flex items-center gap-2 px-2">
          <Calendar className="w-4 h-4 text-cyan-400" />
          <label className="text-sm text-cyan-300 flex-shrink-0">
            Due Date (Optional):
          </label>
          <input
            type="date"
            value={newGoalDeadline}
            onChange={(e) => setNewGoalDeadline(e.target.value)}
            className="bg-gray-700 text-cyan-200 px-3 py-1.5 rounded-xl border-2 border-gray-600 focus:border-yellow-500 outline-none transition-colors text-sm"
          />
          {newGoalDeadline && (
            <button
              onClick={() => setNewGoalDeadline("")}
              className="text-xs text-gray-400 hover:text-cyan-300 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoalsList;
